/**
 * DrawerTabBar — the Card / Email / Files segmented control under the
 * CardDetailDrawer header. Hidden by the caller when only one tab is
 * visible (single-tab cards keep their pre-refactor look).
 *
 * Extracted verbatim from CardDetailDrawer.tsx (operator 500-line ceiling).
 */
import type { ReactNode } from 'react'
import type { CardDetailTab } from '../drawerChrome'

export interface DrawerTab {
  id: CardDetailTab
  label: string
  icon: ReactNode
  visible: boolean
}

export interface DrawerTabBarProps {
  visibleTabs: DrawerTab[]
  safeActiveTab: CardDetailTab
  setActiveTab: (t: CardDetailTab) => void
}

export function DrawerTabBar({ visibleTabs, safeActiveTab, setActiveTab }: DrawerTabBarProps) {
  return (
    <div
      role="tablist"
      aria-label="Card detail tabs"
      className="flex items-center gap-0.5 border-b border-neutral-200 bg-neutral-50 px-2 py-1 dark:border-neutral-800 dark:bg-zinc-900/60"
    >
      {visibleTabs.map((t) => {
        const active = safeActiveTab === t.id
        return (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={active}
            aria-controls={`card-detail-tabpane-${t.id}`}
            onClick={() => setActiveTab(t.id)}
            className={[
              'inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-[12px] font-medium transition-colors',
              active
                ? 'bg-white text-blue-700 shadow-sm ring-1 ring-blue-500/30 dark:bg-zinc-800 dark:text-blue-300'
                : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-100',
            ].join(' ')}
          >
            {t.icon}
            {t.label}
          </button>
        )
      })}
    </div>
  )
}
